import type { Logger as ILogger, LogEntry, LoggerOptions, LogLevel, LogMessage, LogMetadata, Transport } from './logger.config';

import packageJSON from '../../package.json';
import { ApiTransport } from './logger.api';
import { LOG_LEVELS } from './logger.config';
import { ConsoleTransport } from './logger.console';

/**
 * Main logger implementation.
 * Provides logging functionality with support for multiple transports and metadata.
 */
export class Logger implements ILogger {
    /** The name of this logger instance */
    private _name: string;
    /** Optional version of this logger instance */
    private _version: string | undefined;
    /** The current minimum log level */
    private _level: LogLevel;
    /** Array of transports to send logs to */
    private _transports: Transport[];
    /** Default metadata for all log entries */
    private _metadata?: LogMetadata;

    /**
     * Creates a new logger instance.
     * @param options Configuration options for the logger
     */
    constructor(options: LoggerOptions) {
        this._name = options.name;
        this._version = options.version;
        this._level = options.level;
        this._transports = options.transports;
        this._metadata = options.metadata;
    }

    /**
     * Internal method to process and send a log entry.
     * @param level The log level for this entry
     * @param messages The messages to log
     * @param metadata Optional metadata for this log entry
     */
    private log(level: LogLevel, messages: LogMessage, metadata?: LogMetadata): void {
        if (level === 'OFF')
            return;

        const entry: LogEntry = {
            timestamp: new Date(),
            level: LOG_LEVELS[level],
            loggerName: this._name,
            version: this._version,
            messages,
            metadata: {
                ...this._metadata,
                ...metadata,
            },
        };

        // Check if the log level is enabled for this transport
        if (entry.level < LOG_LEVELS[this._level])
            return; // If the log level is not enabled, do not log

        this._transports.forEach(transport => transport.log(entry));
    }

    /** Gets the name of this logger instance */
    get name(): string {
        return this._name;
    }

    /** Gets the current minimum log level */
    get level(): LogLevel {
        return this._level;
    }

    /**
     * Changes the minimum log level for this logger instance.
     * @param level The new minimum log level
     */
    public setLevel(level: LogLevel): void {
        this.info(['Changing log level from', this._level, 'to', level]);
        this._level = level;
    }

    /**
     * Logs messages at the DEBUG level.
     * @param messages Data/messages to log
     * @param metadata Optional metadata to include with this log entry
     */
    public debug(messages: LogMessage, metadata?: LogMetadata): void {
        this.log('DEBUG', messages, metadata);
    }

    /**
     * Logs messages at the INFO level.
     * @param messages Data/messages to log
     * @param metadata Optional metadata to include with this log entry
     */
    public info(messages: LogMessage, metadata?: LogMetadata): void {
        this.log('INFO', messages, metadata);
    }

    /**
     * Logs messages at the WARN level.
     * @param messages Data/messages to log
     * @param metadata Optional metadata to include with this log entry
     */
    public warn(messages: LogMessage, metadata?: LogMetadata): void {
        this.log('WARN', messages, metadata);
    }

    /**
     * Logs messages at the ERROR level.
     * @param messages Data/messages to log
     * @param metadata Optional metadata to include with this log entry
     */
    public error(messages: LogMessage, metadata?: LogMetadata): void {
        this.log('ERROR', messages, metadata);
    }
}

/**
 * Creates a new logger instance with the specified options.
 * @param options Configuration options for the logger
 * @returns A new logger instance
 */
export function createLogger(options: LoggerOptions): ILogger {
    return new Logger(options);
}

/**
 * Default logger instance.
 * Configured with both console and API transports.
 */
export const logger = createLogger({
    name: packageJSON.name,
    version: packageJSON.version,
    level: import.meta.env.dev ? 'DEBUG' : 'INFO',
    transports: [
        new ConsoleTransport({ level: import.meta.env.DEV ? 'DEBUG' : 'INFO' }),
        new ApiTransport({ level: import.meta.env.DEV ? 'OFF' : 'ERROR' }),
    ],
});
